import { useEffect, useState } from "react";
import { Download, Share2, Home, MoreVertical } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Unified PWA install button.
 * - Mobile Android / Desktop Chrome / Edge: triggers the native beforeinstallprompt.
 * - iOS Safari: shows a small "Add to Home Screen" hint modal (Apple doesn't expose a programmatic install).
 * - If already installed (display-mode: standalone), the button hides itself.
 */
export default function InstallButton() {
  const { lang, theme } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS legacy
      window.navigator?.standalone);

  const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent || "");

  useEffect(() => {
    if (isStandalone) {
      setInstalled(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [isStandalone]);

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") setInstalled(true);
      } catch { /* ignore */ }
      setDeferred(null);
      return;
    }
    if (isIos) {
      setShowIosHint(true);
      return;
    }
    setShowIosHint(true); // fallback generic hint
  };

  if (installed) return null;

  const T = lang === "ar"
    ? {
        cta: "تنزيل التطبيق",
        hint_title: "تثبيت التطبيق",
        ios_steps: [
          "في متصفح Safari اضغط زر المشاركة في الأسفل",
          'اختر "إضافة إلى الشاشة الرئيسية"',
          "سيظهر التطبيق كأيقونة مستقلة على جهازك",
        ],
        android_steps: [
          "اضغط على قائمة Chrome (ثلاث نقاط)",
          'اختر "تثبيت التطبيق" أو "إضافة إلى الشاشة الرئيسية"',
          "افتح التطبيق من شاشتك الرئيسية كأي تطبيق آخر",
        ],
        generic_steps: [
          "افتح قائمة المتصفح",
          'اختر "تثبيت التطبيق" أو "Add to Home Screen"',
          "سيُثبَّت التطبيق محلياً على جهازك",
        ],
        close: "حسناً",
      }
    : {
        cta: "Install app",
        hint_title: "Install this app",
        ios_steps: [
          "In Safari, tap the share button at the bottom",
          'Choose "Add to Home Screen"',
          "The app will appear as a standalone icon on your device",
        ],
        android_steps: [
          "Tap the Chrome menu (three dots)",
          'Select "Install app" or "Add to Home Screen"',
          "Open the app from your home screen like any native app",
        ],
        generic_steps: [
          "Open your browser menu",
          'Choose "Install app" or "Add to Home Screen"',
          "The app will be installed locally on your device",
        ],
        close: "Got it",
      };

  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
  const steps = isIos ? T.ios_steps : isAndroid ? T.android_steps : T.generic_steps;
  const stepIcon = isIos ? Share2 : MoreVertical;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="pwa-install-button"
        style={{
          background: theme.goldGradient,
          color: theme.name === "dark" ? "#0a0a0a" : "#fff",
          border: "none",
          padding: "8px 14px",
          borderRadius: "999px",
          fontWeight: 800,
          fontSize: "12px",
          cursor: "pointer",
          display: "inline-flex",
          gap: "6px",
          alignItems: "center",
          letterSpacing: "0.3px",
        }}
      >
        <Download size={14} strokeWidth={2.4} /> {T.cta}
      </button>

      {showIosHint && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
          }}
          onClick={() => setShowIosHint(false)}
          data-testid="pwa-hint-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: theme.surface, border: `1px solid ${theme.borderAccent}`,
              borderRadius: "14px", padding: "20px", maxWidth: "360px", width: "100%",
              color: theme.text, fontFamily: "'Tajawal','Segoe UI',sans-serif",
            }}
          >
            <div style={{ fontWeight: 800, fontSize: "15px", color: theme.gold, marginBottom: "14px", display: "flex", alignItems: "center", gap: 8 }}>
              <Home size={18} strokeWidth={2.2} />
              {T.hint_title}
            </div>
            <ol style={{ fontSize: "13px", lineHeight: 1.9, color: theme.textSoft, paddingInlineStart: 0, listStyle: "none", margin: 0 }}>
              {steps.map((s, i) => {
                const StepIcon = i === 0 ? stepIcon : i === 1 ? Home : Download;
                return (
                  <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                    <span style={{
                      flex: "0 0 26px", height: 26, borderRadius: 999,
                      background: theme.goldGradient,
                      color: theme.name === "dark" ? "#0a0a0a" : "#fff",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 900, fontSize: 12,
                    }}>{i + 1}</span>
                    <span style={{ flex: 1, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <StepIcon size={14} strokeWidth={2} style={{ flexShrink: 0, color: theme.gold }} />
                      {s}
                    </span>
                  </li>
                );
              })}
            </ol>
            <div style={{ marginTop: "16px", textAlign: "end" }}>
              <button
                onClick={() => setShowIosHint(false)}
                data-testid="pwa-hint-close"
                style={{
                  background: theme.goldGradient,
                  color: theme.name === "dark" ? "#0a0a0a" : "#fff",
                  border: "none", padding: "8px 16px", borderRadius: "8px",
                  fontWeight: 800, cursor: "pointer", fontSize: "13px",
                }}
              >
                {T.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
